import { ListItemText, MenuItem } from '@mui/material';
import { InputProps } from '@mui/material/Input';
import { red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { startCase } from 'lodash';
import * as React from 'react';

import InputValidator, { SelectValidator } from '@/components/validators-forms/input-validator';
import * as Types from '@/types';
import { SensorSettingsState } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPanelComponent, TabPropsType } from '.';

const isShown = (state: SensorSettingsState) => {
  return (
    state.peripheralType === 'SENSOR' &&
    state.config?.sensor?.type !== Schema.SensorType.EVENT &&
    state.config?.sensor?.type !== Schema.SensorType.MANUAL_PROCESS
  );
};

const isValid = (state: SensorSettingsState): boolean => {
  switch (state.config?.sensor?.type) {
    case Schema.SensorType.MEASUREMENT:
      return state?.config?.sensor?.sensorUnit?.label && state?.config?.sensor?.sensorUnit?.unit ? true : false;
    default:
      return true;
  }
};

export const TabProps: TabPropsType = {
  isShown,
  isValid,
  label: (t) => t('device:dataSetup', { defaultValue: 'Data' }),
};

const filterTranslationKeys: Record<Schema.DataFilter, [string, string]> = {
  [Schema.DataFilter.AVERAGE_SPEED]: ['shared:averageSpeed', 'Average speed'],
  [Schema.DataFilter.SPEED]: ['shared:speed', 'Speed'],
  [Schema.DataFilter.NONE]: ['shared:none', 'None'],
  [Schema.DataFilter.UPTIME]: ['shared:uptime', 'Uptime'],
};

const filterDescriptionTranslationKeys: Record<Schema.DataFilter, [string, string]> = {
  [Schema.DataFilter.AVERAGE_SPEED]: [
    'shared:averageSpeedFilterDescription',
    'Displays the average speed over the selected scale',
  ],
  [Schema.DataFilter.SPEED]: [
    'shared:speedFilterDescription',
    'Displays the speed between each impulse of produced units',
  ],
  [Schema.DataFilter.NONE]: [
    'shared:noneFilterDescription',
    'Displays impulses of produced units in evenly distributed intervals',
  ],
  [Schema.DataFilter.UPTIME]: [
    'shared:uptimeFilterDescription',
    'Displays the impulses of produced units in two values, 1 for when there is production activity and 0 for when there is not',
  ],
};

const filterOptions = Object.keys(Schema.DataFilter).sort();

export const TabPanel: TabPanelComponent<{ enumTypes: Types.EnumTypes }> = ({
  state,
  handleChange,
  t,
  enumTypes,
  classes,
  invalidFormMessage,
}) => {
  if (!isShown(state)) {
    return null;
  }

  const { config } = state;
  const { sensor } = config || {};
  const { type, sensorUnit, chart, chartTimeScale } = sensor || {};
  const unit = sensorUnit?.unit ? sensorUnit?.unit : 'None';
  const label = sensorUnit?.label || t('units:piecesShort', 'pcs');
  const dataFilter = chart?.dataFilter || Schema.DataFilter.AVERAGE_SPEED;
  const scale = chart?.scale || chartTimeScale || Schema.ChartTimeScale.MINUTE;

  const onSensorUnitChange: InputProps['onChange'] = ({ target: { value } }) => {
    if (!sensorUnit) {
      return;
    }

    handleChange({
      sensor: {
        sensorUnit: {
          ...sensorUnit,
          unit: value === 'None' ? null : (value as Schema.Unit),
        },
      },
    });
  };

  const onSensorUnitLabelChange: InputProps['onChange'] = ({ target: { value } }) => {
    if (!sensorUnit) {
      return;
    }

    handleChange({
      sensor: {
        sensorUnit: {
          ...sensorUnit,
          label: value === 'None' ? null : value,
        },
      },
    });
  };

  const onDataFilterChange: InputProps['onChange'] = ({ target: { value } }) => {
    handleChange({
      sensor: {
        chart: {
          dataFilter: value as Schema.DataFilter,
        },
      },
    });
  };

  const onChartTimeScaleChange: InputProps['onChange'] = ({ target: { value } }) => {
    handleChange({
      sensor: {
        chartTimeScale: value as Schema.ChartTimeScale,
        chart: {
          dataFilter,
          scale: value as Schema.ChartTimeScale,
        },
      },
    });
  };

  const units = enumTypes.units;
  const validators = [];
  const errorMessages = [];

  if (type === Schema.SensorType.MEASUREMENT || type === Schema.SensorType.EVENT) {
    validators.push('required');
    errorMessages.push(t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }));
  }

  return (
    <div className={classes.tabPanel}>
      <SelectValidator
        className={classes.selectFields}
        containerClass={classes.containerClass}
        name="type"
        variant="filled"
        label={`${t(['device:dataUnit'], { defaultValue: 'Data unit' })} *`}
        value={unit}
        helperText={
          type === Schema.SensorType.MEASUREMENT && !unit ? (
            <span style={{ color: red[500] }}>
              {t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })}
            </span>
          ) : (
            ''
          )
        }
        validators={['required']}
        errorMessages={[t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' })]}
        onChange={onSensorUnitChange}
      >
        <MenuItem key="disabled" value="" disabled>
          {t(['device:selectDataUnit'], { defaultValue: 'Select data unit' })}
        </MenuItem>
        {type !== Schema.SensorType.MEASUREMENT && (
          <MenuItem key="none" value="None">
            {t(['shared:none'], { defaultValue: 'None' })}
          </MenuItem>
        )}
        {units?.map((unit) => (
          <MenuItem key={unit.name} value={unit.name}>
            {startCase(unit.name.toLowerCase())}
          </MenuItem>
        ))}
      </SelectValidator>
      <br />
      {typeof unit === 'string' ? (
        <InputValidator
          className={classes.textFields}
          containerClass={classes.containerClass}
          name="dataLabel"
          label={t(['device:dataLabel'], {
            defaultValue: 'Label of the data unit',
          })}
          validators={[...validators, 'isString']}
          errorMessages={[...errorMessages, t(['shared:mustBeString'], { defaultValue: 'must be a string' })]}
          value={sensorUnit?.label ?? ''}
          onChange={onSensorUnitLabelChange}
        />
      ) : null}
      <br />
      {type !== Schema.SensorType.MEASUREMENT && type !== Schema.SensorType.EVENT ? (
        <>
          <SelectValidator
            className={classes.selectFields}
            containerClass={classes.containerClass}
            name="type"
            variant="filled"
            label={t(['device:chartDataFilter'], { defaultValue: 'Chart data filter' })}
            value={dataFilter}
            validators={[...validators]}
            errorMessages={[...errorMessages]}
            onChange={onDataFilterChange}
          >
            {filterOptions.map((filterType) => {
              const translation =
                filterType in filterTranslationKeys
                  ? t(...filterTranslationKeys[filterType as Schema.DataFilter])
                  : filterType;
              const description =
                filterType in filterDescriptionTranslationKeys
                  ? t(...filterDescriptionTranslationKeys[filterType as Schema.DataFilter])
                  : filterType;
              return (
                <MenuItem key={filterType} value={filterType}>
                  <ListItemText primary={translation} secondary={description} />
                </MenuItem>
              );
            })}
          </SelectValidator>
          <br />
          <SelectValidator
            className={classes.selectFields}
            containerClass={classes.containerClass}
            name="type"
            variant="filled"
            label={t(['device:chartSpeedScale'], { defaultValue: 'Chart speed scale' })}
            value={scale}
            validators={[...validators]}
            errorMessages={[...errorMessages]}
            onChange={onChartTimeScaleChange}
          >
            <MenuItem value={Schema.ChartTimeScale.MINUTE}>
              {t(['units:unitPerMinute'], { defaultValue: '[{unit}]/min', unit: label })}
            </MenuItem>
            <MenuItem value={Schema.ChartTimeScale.HOUR}>
              {t(['units:unitPerHour'], { defaultValue: '{{unit}}/hour', unit: label })}
            </MenuItem>
            <MenuItem value={Schema.ChartTimeScale.DAY}>
              {t(['units:unitPerDay'], { defaultValue: '{{unit}}/day', unit: label })}
            </MenuItem>
          </SelectValidator>
        </>
      ) : null}
      {invalidFormMessage}
    </div>
  );
};
